import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CauseProjectCandidate } from '../../../lib/bug-evaluation-api';
import { useCauseProjectCandidates, useUpdateCauseProject } from '../../../hooks/useBugEvaluation';

/** @graph-connects none */
interface CauseProjectCardProps {
  /** 対象のバグチケットの課題キー */
  issueKey: string;
  /** 付け替えできる権限があるか（admin / architect / tech_lead） */
  canEdit: boolean;
}

/**
 * 候補 1 件を出し、クリックで付け替え先に選べるようにする。
 * @graph-connects none
 */
interface CandidateRowProps {
  /** 候補のチャンク */
  candidate: CauseProjectCandidate;
  /** 今選ばれている付け替え先 */
  selected: string;
  /** 候補を選んだとき */
  onPick: (issueKey: string) => void;
}

/**
 * 付け替え先候補 1 件を、根拠の PR つきで描画する。
 *
 * **PR の件数とリポジトリを値として主役に置く。** どのチャンクかは「そのコードを作った PR が
 * どのチャンクのものか」で決まるので、人が最初に見るのはそこ。PR が 0 件のチャンクは
 * そのコードを作っていないため、薄く出して選びにくくする（消さないのは「PR が無い」ことも
 * 候補から外す根拠だから）。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 起因案件の付け替え先候補を PR の件数・リポジトリ・代表 PR つきで並べ、クリックで選べるようにする
 * @graph-connects none
 */
function CandidateRow({ candidate, selected, onPick }: CandidateRowProps) {
  const { t } = useTranslation();
  const isSelected = selected === candidate.issueKey;
  const hasPr = candidate.prCount > 0;

  return (
    <li className="border-t border-[var(--color-border)] py-2 first:border-t-0">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
        <button
          type="button"
          aria-label={candidate.issueKey}
          aria-pressed={isSelected}
          onClick={() => {
            onPick(candidate.issueKey);
          }}
          className={`self-start rounded-md border px-2 py-0.5 text-left text-sm ${
            isSelected
              ? 'border-[var(--color-accent-primary)] text-[var(--color-accent-primary)]'
              : 'border-[var(--color-border)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
          }`}
        >
          {candidate.issueKey}
        </button>
        <span
          className={
            hasPr
              ? 'text-sm text-[var(--color-text-primary)]'
              : 'text-sm text-[var(--color-text-muted)]'
          }
        >
          {hasPr
            ? `${String(candidate.prCount)} PR ・ ${candidate.repositories.join(', ')}`
            : t('bugEvaluation.causeProjectNoPr', 'PR なし')}
        </span>
      </div>
      <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
        {candidate.summary ?? ''}
        {candidate.releasedAt !== null && ` ・ ${candidate.releasedAt.slice(0, 10)}`}
      </p>
      {candidate.pullRequests.length > 0 && (
        <ul className="mt-1 space-y-0.5 pl-2">
          {candidate.pullRequests.map((pr) => (
            <li key={`${pr.repository}#${String(pr.number)}`} className="text-xs">
              {pr.url === null ? (
                <span className="text-[var(--color-text-secondary)]">
                  {pr.repository}#{pr.number}
                </span>
              ) : (
                <a
                  href={pr.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--color-accent-primary)] hover:underline"
                >
                  {pr.repository}#{pr.number}
                </a>
              )}{' '}
              <span className="text-[var(--color-text-secondary)]">{pr.title ?? ''}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * 起因案件の確認と付け替えを行うカード。
 *
 * i-point は**起因案件の開発リーダー個人**に紐づいて残るため、起因案件が誤っていると書いて
 * いない人にスコアが付く。置けない課題種別（統合プロダクト等）が入っていれば API が理由を
 * 返すので、それをそのまま見せる（置ける種別の一覧を画面に写さない——規則が変わったときに
 * 黙ってずれる）。
 *
 * **付け替え先は PR で決まる。** 統合プロダクトが入っていた場合の候補には、チャンクごとの
 * マージ済み PR の件数・リポジトリ・代表例が付く。人が勘で選ぶのではなく、起因リポジトリと
 * 突き合わせて選ぶための材料。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 起因案件の現状と置けない理由を示し、PR証跡つきの候補から付け替え先を選んで確定する
 * @graph-connects useCauseProjectCandidates [calls] 起因案件の現状と付け替え先候補の取得
 * @graph-connects useUpdateCauseProject [calls] 起因案件の付け替え
 */
export function CauseProjectCard({ issueKey, canEdit }: CauseProjectCardProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useCauseProjectCandidates(issueKey, canEdit);
  const update = useUpdateCauseProject(issueKey);
  const [picked, setPicked] = useState('');
  const [note, setNote] = useState('');

  if (!canEdit || isLoading || !data) return null;

  const { current, rejection, candidates, integratedProductPrCount } = data.data;
  const allCandidatesLackPr =
    candidates.length > 0 && candidates.every((candidate) => candidate.prCount === 0);

  return (
    <Card className="p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
        <h2 className="text-sm font-medium text-[var(--color-text-secondary)]">
          {t('bugEvaluation.causeProject', '起因案件')}
        </h2>
        {current !== null && (
          <span className="text-sm text-[var(--color-text-primary)]">
            {current.issueKey}
            <span className="ml-2 text-xs text-[var(--color-text-muted)]">
              {current.issueTypeName ?? ''}
            </span>
          </span>
        )}
      </div>

      {current === null && (
        <p className="mt-2 text-sm text-[var(--color-text-muted)]">
          {t('bugEvaluation.causeProjectUnset', '起因案件が設定されていません')}
        </p>
      )}

      {rejection !== null && (
        <p className="mt-2 rounded-md border border-[var(--color-warning)] p-2 text-xs text-[var(--color-text-primary)]">
          {rejection.message}
        </p>
      )}

      {candidates.length > 0 && (
        <ul className="mt-3">
          {candidates.map((candidate) => (
            <CandidateRow
              key={candidate.issueKey}
              candidate={candidate}
              selected={picked}
              onPick={setPicked}
            />
          ))}
        </ul>
      )}

      {allCandidatesLackPr && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          {integratedProductPrCount > 0
            ? t('bugEvaluation.causeProjectPrOnIntegrated', {
                defaultValue:
                  'どの候補にも PR がありません。{{count}} 件の PR が統合プロダクトのキーで記録されていてチャンクへ辿れないため、付け替え先はコードを読んで決めることになります。',
                count: integratedProductPrCount,
              })
            : t(
                'bugEvaluation.causeProjectNoPrAtAll',
                'どの候補にも PR がなく、統合プロダクトのキーにも PR がありません。この案件では GitHub 上の変更が記録されていません。',
              )}
        </p>
      )}

      <div className="mt-3 flex flex-col gap-2">
        <input
          type="text"
          value={picked}
          onChange={(event) => {
            setPicked(event.target.value);
          }}
          aria-label={t('bugEvaluation.causeProjectKey', '付け替え先の課題キー')}
          placeholder="AIRCLOSET-87861"
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1 text-sm text-[var(--color-text-primary)]"
        />
        <input
          type="text"
          value={note}
          onChange={(event) => {
            setNote(event.target.value);
          }}
          aria-label={t('bugEvaluation.causeProjectNote', '付け替えの理由')}
          placeholder={t(
            'bugEvaluation.causeProjectNotePlaceholder',
            '理由（Backlog のコメントに残ります）',
          )}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1 text-sm text-[var(--color-text-primary)]"
        />
        <button
          type="button"
          disabled={picked.trim() === '' || update.isPending}
          onClick={() => {
            update.mutate(
              {
                causeProjectKey: picked.trim(),
                note: note.trim() === '' ? undefined : note.trim(),
              },
              {
                onSuccess: () => {
                  setPicked('');
                  setNote('');
                },
              },
            );
          }}
          className="self-start rounded-md bg-[var(--color-accent-primary)] px-3 py-1 text-sm text-white hover:bg-[var(--color-accent-hover)] disabled:opacity-50"
        >
          {t('bugEvaluation.causeProjectSubmit', '起因案件を付け替える')}
        </button>
      </div>

      {update.isError && (
        <p className="mt-2 text-xs text-[var(--color-error)]">
          {update.error instanceof Error
            ? update.error.message
            : t('bugEvaluation.causeProjectFailed', '付け替えに失敗しました')}
        </p>
      )}
      {update.isSuccess && (
        <p className="mt-2 text-xs text-[var(--color-success)]">
          {t(
            'bugEvaluation.causeProjectUpdated',
            'Backlog へ書き込みました。評価一覧への反映は同期待ちです。',
          )}
        </p>
      )}
    </Card>
  );
}
